/**
 * v-spaceListHotkey
 * @desc 列表响应快捷键
 * @example
 * ```vue
 * <div v-spaceListHotkey/>
 * ```
 */
import { DirectiveBinding } from 'vue/types/options.d';
import { nextTick } from '@vue/composition-api';
import { debounce, throttle } from 'lodash';
import hotkeyDataHelper from '@/renderer/scripts/utils/hotkeyDataHelper';
import ipc from '../scripts/utils/ipc';
let callback: ((action: string) => boolean) | null = null;
const bindClassName = new Set<Element>();
function focusList() {
  bindClassName.forEach((element, sameElement, set) => {
    (element as HTMLElement).focus({
      preventScroll: true,
    });
  });
}
const throttleFocusList = throttle(focusList, 500);
function handleKeyDown(event: KeyboardEvent) {
  if (!document.activeElement || !bindClassName.has(document.activeElement)) {
    return;
  }
  if (['Meta', 'Control', 'Shift'].includes(event.key)) {
    return;
  }
  const action = hotkeyDataHelper.getHotkeyAction(event);
  if (action !== '' && callback) {
    if (callback(action) === true) {
      event.preventDefault();
    }
  }
}

function isActiveElementShouldConcern() {
  if (!document.activeElement) {
    // 过滤无焦点元素的事件
    return false;
  }

  if (
    !(
      document.activeElement.hasAttribute('readOnly') && document.activeElement.getAttribute('readOnly') === 'readonly'
    ) &&
    (document.activeElement.className !== '' || document.activeElement.tagName !== 'BODY')
  ) {
    // 过滤 非readonly 且 className为空 ， tagName不为BODY 的控件，这类控件一般为input
    return false;
  }
  return true;
}

function isTDesignDialogOpen() {
  if (
    [...document.getElementsByClassName('t-dialog__ctx')].filter(e => (e as HTMLElement).style.display !== 'none')
      .length > 0
  ) {
    return true;
  }
  return false;
}

const debounceFocusChanged = debounce((event: FocusEvent) => {
  focusChanged(event);
}, 200);

function focusChanged(event: FocusEvent) {
  nextTick(() => {
    if (
      !isActiveElementShouldConcern() ||
      (event.relatedTarget && event.type !== 'focusout') || // 过滤含相关控件且不是focusout的事件
      bindClassName.size < 0 ||
      isTDesignDialogOpen()
    ) {
      return;
    }
    throttleFocusList();
  });
}

export default {
  bind(el: HTMLElement, binding: DirectiveBinding): void {
    callback = binding.value;
    if (el.tabIndex < 0) {
      console.error('hotkey element must have tabIndex >= 0');
    }
    bindClassName.add(el);
    el.addEventListener('keydown', handleKeyDown);
    el.addEventListener('focusout', debounceFocusChanged);
    if (bindClassName.size === 1) {
      ipc.on(ipc.RENDERER_EVENTS_MAP.FOCUS_LIST, throttleFocusList);
    }
    nextTick(() => {
      throttleFocusList();
    });
  },
  unbind(el: HTMLElement): void {
    bindClassName.delete(el);
    el.removeEventListener('keydown', handleKeyDown);
    el.removeEventListener('focusout', debounceFocusChanged);
    if (bindClassName.size <= 0) {
      ipc.off(ipc.RENDERER_EVENTS_MAP.FOCUS_LIST, throttleFocusList);
    }
  },
};
